import type { Action } from '@reduxjs/toolkit';

import { REHYDRATE } from 'redux-persist';

/**
 * Shared `extractRehydrationInfo` for every RTK Query API.
 *
 * redux-persist is only wired up in local dev (see `~/dev/reduxPersist` and
 * `~/store`). This handler tells RTK Query how to pick up its own slice of the
 * persisted state when the `REHYDRATE` action fires, so cached queries are
 * served from localStorage after a reload instead of refetching.
 *
 * Outside of dev the `REHYDRATE` action is never dispatched, so this is inert
 * in production — it just returns `undefined` for every action.
 */
interface RehydrateAction {
  type: typeof REHYDRATE;
  key: string;
  payload?: Record<string, unknown>;
}

function isRehydrateAction(action: Action): action is RehydrateAction {
  return action.type === REHYDRATE;
}

export function extractRtkQueryRehydrationInfo<TState>(
  action: Action,
  { reducerPath }: { reducerPath: string },
): TState | undefined {
  if (isRehydrateAction(action)) {
    return action.payload?.[reducerPath] as TState | undefined;
  }

  return undefined;
}
